'use client'

import React from 'react'
import { useFormStatus } from 'react-dom'
import { MinimalisticMagnifer } from 'solar-icons'
import { useIsMobile } from '@/lib/hooks'
import { cn } from '@/lib/utils'

import {
  Button,
  KeyChip,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui'
import { FloatingKeyboard } from '@/components/ui/keyboard'

interface SearchBarProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onChangeValue?: (value: string) => void
  isCustomerSearch?: boolean
}

export const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(
  ({ className, onChangeValue, type, isCustomerSearch, ...props }, ref) => {
    const { pending } = useFormStatus()
    const isMobile = useIsMobile()
    const [inputValue, setInputValue] = React.useState('')
    const showVirtualKeyboard = localStorage?.getItem('showVirtualKeyboard')

    const handleKeyPress = (value: string) => {
      if (value === '⌫') {
        const newValue = inputValue.slice(0, -1)
        setInputValue(newValue)
        onChangeValue?.(newValue)
      } else if (value === 'clear') {
        setInputValue('')
        onChangeValue?.('')
      } else {
        setInputValue((prev) => prev + value)
        onChangeValue?.(inputValue + value)
      }
    }

    return (
      <Popover>
        <div
          className={cn(
            'flex w-full flex-col items-center gap-1 overflow-hidden rounded-lg border border-gray-100 bg-gray-50 px-1 ring-gray-700 focus-within:bg-white focus-within:ring-2 lg:h-[52px] lg:flex-row dark:border-gray-700 dark:bg-gray-900',
            className,
          )}
        >
          <PopoverTrigger asChild>
            <div className="flex h-full w-full items-center py-4 lg:py-0">
              <span className="px-2">
                <MinimalisticMagnifer className="size-4" strokeWidth={1.5} />
              </span>
              <input
                type={type}
                ref={ref}
                placeholder="Enter Product name, Barcode, or SKU"
                className="h-full flex-grow bg-transparent text-sm font-normal placeholder-gray-400 focus-visible:outline-none"
                autoCorrect="off"
                spellCheck={false}
                {...props}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value)
                  onChangeValue?.(e.target.value)
                }}
              />
            </div>
          </PopoverTrigger>

          <div className="flex h-full w-full py-1 lg:w-auto">
            <Button
              disabled={pending}
              aria-disabled={pending}
              className="w-full gap-2 rounded-md lg:h-full lg:w-auto lg:pr-2"
              tabIndex={-1}
              type="submit"
            >
              <span>{pending ? 'Searching' : 'Search'}</span>
              <KeyChip variant="lighter" shortcut="enter" className="h-full" />
            </Button>
          </div>
        </div>

        {!isMobile && showVirtualKeyboard === 'true' && (
          <PopoverContent
            className={cn(
              'z-[500] w-[var(--radix-popover-trigger-width)] p-0',
              {
                '!min-w-[700px]': isCustomerSearch,
              },
            )}
            sideOffset={4}
          >
            <FloatingKeyboard
              onKeyPress={handleKeyPress}
              type={type ?? 'text'}
              isNumberOnly={type === 'number'}
            />
          </PopoverContent>
        )}
      </Popover>
    )
  },
)

SearchBar.displayName = 'SearchBar'
